import type { NotificationList, Waiting } from '@myrewards/contracts';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  dismissNotice,
  markAllNoticesRead,
  markNoticeRead,
  notificationKeys,
  notificationsQuery,
  unreadCountQuery,
} from '../api/notifications';
import { bellSetupItems, bellWaitingItems } from '../home/waiting';
import type { BellNotice, BellState } from '../shell';
import { formatTimestamp } from '../ui';

type Pages = InfiniteData<NotificationList>;

/** Only app paths open from the bell: anything else goes to Home. */
function safeHref(href: string): string {
  return href.startsWith('/') && !href.startsWith('//') && !href.startsWith('/\\') ? href : '/';
}

/** Notices from every loaded page, newest first. */
export function bellNotices(pages: Pages | undefined): BellNotice[] {
  return (pages?.pages ?? []).flatMap((page) =>
    page.items.map((n) => ({
      id: n.id,
      title: n.title,
      ...(n.body !== null && { body: n.body }),
      when: formatTimestamp(n.createdAt),
      href: safeHref(n.href),
      read: n.read,
    })),
  );
}

function updatePages(
  pages: Pages | undefined,
  change: (items: NotificationList['items']) => NotificationList['items'],
): Pages | undefined {
  if (pages === undefined) return undefined;
  return { ...pages, pages: pages.pages.map((p) => ({ ...p, items: change(p.items) })) };
}

/**
 * The bell (N4): the person's unread count (kept fresh while the app is open), their notices
 * once the panel opens, and the getting-started steps from Waiting on you. Reading or dismissing
 * a notice shows at once and is confirmed by the server; Waiting on you is untouched.
 */
export function useBell(waiting: Waiting | undefined): BellState {
  const queryClient = useQueryClient();
  const [opened, setOpened] = useState(false);
  const count = useQuery(unreadCountQuery);
  const list = useInfiniteQuery({ ...notificationsQuery, enabled: opened });

  const settle = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };
  const adjustCount = (by: number): void => {
    queryClient.setQueryData(notificationKeys.count, (old: { unread: number } | undefined) =>
      old === undefined ? old : { unread: Math.max(0, old.unread + by) },
    );
  };

  const read = useMutation({
    mutationFn: markNoticeRead,
    onMutate: (id: string) => {
      queryClient.setQueryData<Pages>(notificationKeys.list, (pages) =>
        updatePages(pages, (items) => items.map((n) => (n.id === id ? { ...n, read: true } : n))),
      );
      adjustCount(-1);
    },
    onSettled: settle,
  });

  const dismiss = useMutation({
    mutationFn: dismissNotice,
    onMutate: (id: string) => {
      const wasUnread = bellNotices(queryClient.getQueryData<Pages>(notificationKeys.list)).some(
        (n) => n.id === id && !n.read,
      );
      queryClient.setQueryData<Pages>(notificationKeys.list, (pages) =>
        updatePages(pages, (items) => items.filter((n) => n.id !== id)),
      );
      if (wasUnread) adjustCount(-1);
    },
    onSettled: settle,
  });

  const readAll = useMutation({
    mutationFn: markAllNoticesRead,
    onMutate: () => {
      queryClient.setQueryData<Pages>(notificationKeys.list, (pages) =>
        updatePages(pages, (items) => items.map((n) => ({ ...n, read: true }))),
      );
      queryClient.setQueryData(notificationKeys.count, { unread: 0 });
    },
    onSuccess: (answer) => {
      queryClient.setQueryData(notificationKeys.count, answer);
    },
    onSettled: settle,
  });

  const gettingStarted = useMemo(() => bellSetupItems(waiting?.items ?? []), [waiting]);
  const waitingWork = useMemo(() => bellWaitingItems(waiting?.items ?? []), [waiting]);
  const notices = useMemo(() => bellNotices(list.data), [list.data]);

  return {
    notices,
    unread: count.data?.unread ?? list.data?.pages[0]?.unread ?? 0,
    gettingStarted,
    waiting: waitingWork,
    status: list.isError ? 'error' : list.data === undefined ? 'loading' : 'ready',
    hasMore: list.hasNextPage,
    loadingMore: list.isFetchingNextPage,
    onOpenChange: (open) => {
      if (open) setOpened(true);
    },
    onOpenNotice: (id) => {
      read.mutate(id);
    },
    onDismiss: (id) => {
      dismiss.mutate(id);
    },
    onMarkAllRead: () => {
      readAll.mutate();
    },
    onLoadMore: () => {
      void list.fetchNextPage();
    },
  };
}
